import React from 'react';
import useAxios from '../api/useAxios';
import { useToast } from '../context/ToastContext';

interface DownloadOptions {
  url: string;
  filename?: string;
  mimeType?: string;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  successMessage?: string;
  errorMessage?: string;
}

interface UseDownloadReturn {
  downloadFile: () => Promise<void>;
  loading: boolean;
  error: any;
}

export const useDownload = (options: DownloadOptions): UseDownloadReturn => {
  const {
    url,
    filename,
    mimeType = 'application/octet-stream',
    showSuccessToast = true,
    showErrorToast = true,
    successMessage = 'File downloaded successfully!',
    errorMessage = 'Failed to download file',
  } = options;

  const { showToast } = useToast();
  const [shouldDownload, setShouldDownload] = React.useState(false);

  const {
    data: downloadData,
    error: downloadError,
    loading,
    fetchData: fetchDownloadData,
  } = useAxios<any>(url, 'GET', 'blob');

  const downloadFile = async (): Promise<void> => {
    if (!url) {
      if (showErrorToast) {
        showToast('Download URL is required', 'error');
      }
      return;
    }

    setShouldDownload(true);
    try {
      await fetchDownloadData();
    } catch (error) {
      console.error('Error downloading file:', error);
      if (showErrorToast) {
        showToast(errorMessage, 'error');
      }
      setShouldDownload(false);
    }
  };

  React.useEffect(() => {
    if (downloadData && shouldDownload) {
      try {
        const blob = new Blob([downloadData], { type: mimeType });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        if (showSuccessToast) {
          showToast(successMessage, 'success');
        }
      } catch (error) {
        console.error('Error creating download:', error);
        if (showErrorToast) {
          showToast('Failed to create download', 'error');
        }
      } finally {
        setShouldDownload(false);
      }
    }
  }, [
    downloadData,
    shouldDownload,
    mimeType,
    filename,
    showSuccessToast,
    showErrorToast,
    successMessage,
    showToast,
  ]);

  React.useEffect(() => {
    if (downloadError && shouldDownload && showErrorToast) {
      showToast(errorMessage, 'error');
      setShouldDownload(false);
    }
  }, [downloadError, shouldDownload, showErrorToast, errorMessage, showToast]);

  return {
    downloadFile,
    loading,
    error: downloadError,
  };
};

// Specific download utilities for common file types
export const useExcelDownload = (options: Omit<DownloadOptions, 'mimeType'>) => {
  return useDownload({
    ...options,
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

export const useCSVDownload = (options: Omit<DownloadOptions, 'mimeType'>) => {
  return useDownload({
    ...options,
    mimeType: 'text/csv',
  });
};

export const usePDFDownload = (options: Omit<DownloadOptions, 'mimeType'>) => {
  return useDownload({
    ...options,
    mimeType: 'application/pdf',
  });
};
